import { useQuery } from '@tanstack/react-query';
import { isCategoryId, toolsFor, type CategoryId, type ToolId } from '@dale/plans';

import { getMe, ME_QUERY_KEY } from '../api/endpoints/permissions';
import { useSession } from '../auth/useSession';
import { useBusiness } from './useBusiness';

/** Same freshness as the features: a tool the owner switches shows on the next refresh everywhere. */
const TOOLS_STALE_MS = 30_000;

export type Tools = {
  /** Which tools show: the server's answer, or the business type's defaults until it arrives. */
  tools: Readonly<Record<ToolId, boolean>>;
  /** The business type, for each tool's name; undefined while the business loads. */
  category: CategoryId | undefined;
  /** True once the business and the server's answer are both in (or the answer failed): no more guessing. */
  ready: boolean;
};

/**
 * Herramientas for the signed-in business: the class studio's tools every type
 * gets (Calendario, planes, spaces, group sessions), as the owner chose them.
 */
export function useTools(): Tools {
  const { status } = useSession();
  const business = useBusiness();
  const me = useQuery({ queryKey: ME_QUERY_KEY, queryFn: getMe, enabled: status === 'authenticated', staleTime: TOOLS_STALE_MS });
  const category = business && isCategoryId(business.category) ? business.category : undefined;
  return {
    tools: me.data?.data.tools ?? toolsFor(category, {}),
    category,
    ready: business !== undefined && (me.data !== undefined || me.isError),
  };
}
